import { useMemo, useState } from 'react';
import { Formik, Field } from 'formik';
import {
    Box,
    Flex,
    HStack,
    VStack,
    Avatar,
    Badge,
    Switch,
    Text,
    Stack,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    CloseButton,
} from '@chakra-ui/react';
import {
    Button,
    FormControl,
    FormLabel,
    Input,
    Tooltip,
} from '@/components/v4';
import { Select as NumberSelect } from 'chakra-react-select';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
    dropDownFormStyle,
} from 'components/forms/new/formStyles';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import useGetCompanyServices from '@/queries/appAuth/useGetCompanyServices';
import useGetAvailableNumbersDropdown from 'pages/accounts/users/queries/useGetAvailableNumbersDropdown';
import getEightByEightCatalogue from 'API/ServiceProfiles/getEightByEightCatalogue';
import { isRequiredRule } from 'utils/eightByEightCatalog';
import {
    createUserProfile,
    deleteUserProfile,
    getLicenses,
    getUserProfile,
    updateUserProfile,
    updatePlatformUser,
} from 'pages/services/eightByEight/userManagement/api';
import AddUserLicenceTree from './add/steps/AddUserLicenceTree';

// const TIMEZONES = [
//     { value: 'Europe/London', label: 'Europe/London' },
//     { value: 'America/New_York', label: 'America/New_York' },
//     { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
//     { value: 'America/Chicago', label: 'America/Chicago' },
//     { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
//     { value: 'Australia/Sydney', label: 'Australia/Sydney' },
// ];

// const LANGUAGES = [
//     { value: 'English (UK)', label: 'English (UK)' },
//     { value: 'English (US)', label: 'English (US)' },
//     { value: 'French', label: 'French' },
//     { value: 'German', label: 'German' },
//     { value: 'Spanish', label: 'Spanish' },
// ];

const NOT_ALLOCATED_OPTION = {
    numberID: '',
    telephoneNumber: '— Not Allocated —',
};

const AccordionSectionButton = ({ children, darkMode }) => (
    <AccordionButton
        px={4}
        py={3}
        bg={darkMode ? 'dark.tcap' : undefined}
        _expanded={{ bg: darkMode ? 'dark.tcap' : undefined }}
        _hover={darkMode ? { bg: 'dark.bgDarkGray' } : {}}
        sx={
            darkMode
                ? {}
                : {
                      backgroundColor: 'white !important',
                      '&[aria-expanded="true"]': {
                          backgroundColor: 'white !important',
                      },
                      '&:hover': {
                          backgroundColor:
                              'var(--chakra-colors-gray-50) !important',
                      },
                  }
        }>
        <Box
            flex="1"
            textAlign="left"
            fontWeight="600"
            fontSize="sm"
            color={darkMode ? 'white' : 'gray.700'}>
            {children}
        </Box>
        <AccordionIcon color={darkMode ? 'white' : 'gray.600'} />
    </AccordionButton>
);

const getProfileLicenses = (profile) => profile?.licenses || [];

const getProfileNumber = (profile) => profile?.platformNumber || profile;

const getProfileNumberId = (profile) =>
    getProfileNumber(profile)?.numberID || profile?.numberID || '';

const getLicenseId = (license) =>
    license?.id || license?.itemID || license?.catalogItemID;

const getSelectedLicenceIds = (...profiles) =>
    profiles
        .map(getProfileLicenses)
        .find((licenses) => licenses.length > 0)
        ?.map(getLicenseId)
        .filter(Boolean) || [];

export default function EightByEightUserEditContent({
    selectedPlatform,
    onClose,
    onSave,
    darkMode,
}) {
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { requiredScope } = useSelector((state) => state.login);

    const { data: companyServices = [] } =
        useGetCompanyServices(currentCompany);
    const eightByEightService = companyServices.find(
        (s) => s.serviceType === 'EightByEight',
    );
    const serviceId = eightByEightService?.id;
    const platformId = eightByEightService?.platformID;

    // primaryUserID is the 8x8 user ID used by the UserProfiles endpoint
    const platformUserId =
        selectedPlatform?.primaryUserID || selectedPlatform?.id;

    const { data: userProfile } = useQuery({
        queryKey: ['8x8-drawer-userProfile', platformUserId],
        queryFn: () =>
            getUserProfile(platformUserId, requiredScope).then((r) => r.data),
        enabled: !!platformUserId,
        staleTime: 0,
        cacheTime: 0,
    });

    const { data: rawLicenses = [] } = useQuery({
        queryKey: ['8x8-drawer-licences', serviceId],
        queryFn: () =>
            getLicenses(serviceId, requiredScope).then((r) => r.data),
        enabled: !!serviceId,
        staleTime: 0,
        cacheTime: 0,
    });

    // getLicenses only returns flat per-item quantities (catalogItemID,
    // allowedQuantity, usedQuantity) — parent/child hierarchy isn't part of
    // that response, so it has to come from the company's available catalogue.
    const { data: catalogueItems = [] } = useQuery({
        queryKey: ['8x8-drawer-catalogue', currentCompany],
        queryFn: () =>
            getEightByEightCatalogue({
                level: 'company',
                companyID: currentCompany,
            }).then((r) => r.data || []),
        enabled: !!currentCompany,
        staleTime: 0,
        cacheTime: 0,
    });

    // Merge the catalogue hierarchy with per-item usage quantities into the
    // shape AddUserLicenceTree expects.
    const catalogue = useMemo(() => {
        const usageByItemId = {};
        rawLicenses.forEach((l) => {
            const itemId = l.catalogItemID ?? l.itemID ?? l.id;
            if (itemId) usageByItemId[itemId] = l;
        });
        return catalogueItems.map((item) => {
            const usage = usageByItemId[item.id];
            return {
                id: item.id,
                name: item.name,
                description: item.description || '',
                parentId: item.parentId || null,
                active: usage?.usedQuantity ?? 0,
                total: usage?.allowedQuantity ?? 0,
                required: isRequiredRule(item),
            };
        });
    }, [catalogueItems, rawLicenses]);

    const selectedUserProfile = selectedPlatform?.userProfiles?.[0];
    const primaryNumberId =
        getProfileNumberId(selectedUserProfile) ||
        getProfileNumberId(userProfile);
    const resolvedPlatformNumber = primaryNumberId
        ? getProfileNumber(selectedUserProfile) || getProfileNumber(userProfile)
        : undefined;
    const platformNumber = primaryNumberId
        ? {
              ...resolvedPlatformNumber,
              numberID: primaryNumberId,
          }
        : undefined;

    const [rawNumberList, setRawNumberList] = useState([]);
    const { isLoading: isLoadingNumbers } = useGetAvailableNumbersDropdown(
        currentCompany,
        platformNumber,
        platformId,
        setRawNumberList,
    );
    // Strip the "- Not Allocated -" sentinel (it uses id:'null', not numberID)
    const numberList = rawNumberList.filter((n) => n.id !== 'null');

    // Full number list: available numbers + currently assigned number (if not in list) + Not Allocated
    const fullNumberList = useMemo(() => {
        const list = [...numberList];
        if (
            userProfile?.numberID &&
            !list.some((n) => n.numberID === userProfile.numberID)
        ) {
            list.unshift({
                numberID: userProfile.numberID,
                telephoneNumber: userProfile.numberID,
            });
        }
        list.push(NOT_ALLOCATED_OPTION);
        return list;
    }, [numberList, userProfile?.numberID]);

    const borderColor = darkMode ? 'dark.tcap-borderGray' : 'gray.200';
    const panelBg = darkMode ? 'dark.tcap' : 'white';
    const accordionBorderColor = darkMode ? 'dark.tcap-borderGray' : 'gray.200';

    const pu = selectedPlatform?.primaryUser || selectedPlatform;

    const displayName =
        pu?.displayName ||
        [pu?.givenName, pu?.surname].filter(Boolean).join(' ') ||
        'User';

    const initialValues = {
        firstName: pu?.givenName || '',
        lastName: pu?.surname || '',
        email: pu?.userPrincipalName || pu?.email || '',
        siteId: selectedPlatform?.siteId || '',
        timezone: selectedPlatform?.timezone || 'Europe/London',
        language: selectedPlatform?.language || 'English (UK)',
        displayInDirectory: selectedPlatform?.displayInDirectory ?? true,
        managedByTCAP: selectedPlatform?.managedByTCAP ?? true,
        // Contact Details
        officePhone: selectedPlatform?.officePhone || '',
        mobilePhone: selectedPlatform?.mobilePhone || '',
        extension: selectedPlatform?.extension || '',
        // Licences — pre-fill from userProfile
        selectedLicenceIds: getSelectedLicenceIds(
            selectedUserProfile,
            userProfile,
        ),
        // Phone Numbers — pre-fill from userProfile
        primaryNumberId,
        additionalNumberIds: selectedPlatform?.additionalNumberIds || [],
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            // Managed by Other — this user's data is locked; no changes go out.
            if (!values.managedByTCAP) {
                if (typeof onSave === 'function') onSave(values);
                return;
            }

            const numberCleared = !!primaryNumberId && !values.primaryNumberId;

            // Step 1: Update user details
            await updatePlatformUser(
                {
                    id: platformUserId,
                    platformID: platformId,
                    givenName: values.firstName,
                    surname: values.lastName,
                    email: values.email,
                    displayName:
                        `${values.firstName} ${values.lastName}`.trim(),
                    managedBy: 0,
                },
                requiredScope,
            );

            // Step 2: DELETE if number was cleared
            if (numberCleared) {
                await deleteUserProfile(
                    primaryNumberId,
                    platformId,
                    platformUserId,
                    requiredScope,
                );
            }

            // Step 3: PUT if a number is selected (always sync number + licences)
            //         OR if licences were selected with no number (licences-only update)
            const licences = values.selectedLicenceIds.map((id) => {
                const match = catalogue.find((l) => l.id === id);
                return {
                    id,
                    name: match?.name || '',
                    description: match?.description || null,
                };
            });

            const profilePayload = {
                platformUserID: platformUserId,
                platformID: platformId,
                licenses: licences,
            };

            if (values.primaryNumberId) {
                const payloadWithNumber = {
                    ...profilePayload,
                    numberID: values.primaryNumberId,
                };

                if (primaryNumberId) {
                    await updateUserProfile(payloadWithNumber, requiredScope);
                } else {
                    await createUserProfile(payloadWithNumber, requiredScope);
                }
            } else if (!numberCleared && licences.length > 0) {
                // No number ever assigned — still persist licence selections
                if (primaryNumberId) {
                    await updateUserProfile(profilePayload, requiredScope);
                } else {
                    await createUserProfile(profilePayload, requiredScope);
                }
            }

            toast.success('User updated successfully');
            if (typeof onSave === 'function') onSave(values);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'An error occurred. Please try again.';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize>
            {({ values, setFieldValue, handleSubmit: submitForm }) => {
                // Any root-level (no-parent) licence stands in for "base
                // platform assigned" — a phone number can't be allocated
                // without one.
                const hasBaseLicence = catalogue.some(
                    (item) =>
                        !item.parentId &&
                        values.selectedLicenceIds.includes(item.id),
                );
                return (
                    <Flex direction="column" h="100%" gap={0}>
                        {/* Header */}
                        <Flex
                            align="center"
                            justify="space-between"
                            pb={4}
                            mb={2}
                            borderBottomWidth="1px"
                            borderColor={borderColor}>
                            <HStack spacing={3}>
                                <Avatar
                                    size="md"
                                    bg="messenger.500"
                                    color="white"
                                    name={displayName}
                                />
                                <VStack align="flex-start" spacing={0}>
                                    <HStack spacing={2}>
                                        <Text
                                            fontWeight="700"
                                            fontSize="lg"
                                            color={
                                                darkMode ? 'white' : 'gray.800'
                                            }>
                                            {displayName}
                                        </Text>
                                        <Badge
                                            colorScheme="messenger"
                                            fontSize="10px"
                                            px={2}
                                            py="2px"
                                            borderRadius="md"
                                            textTransform="uppercase">
                                            TCAP
                                        </Badge>
                                    </HStack>
                                    {(pu?.userPrincipalName || pu?.email) && (
                                        <Text fontSize="xs" color="gray.500">
                                            {pu.userPrincipalName || pu.email}
                                        </Text>
                                    )}
                                </VStack>
                            </HStack>
                            <CloseButton
                                size="md"
                                onClick={onClose}
                                color={darkMode ? 'white' : 'gray.600'}
                            />
                        </Flex>

                        {/* Managed By TCAP toggle */}
                        <Flex
                            align="center"
                            justify="space-between"
                            px={1}
                            py={3}
                            mb={3}
                            borderBottomWidth="1px"
                            borderColor={borderColor}>
                            <Text
                                fontSize="sm"
                                fontWeight="600"
                                color={darkMode ? 'white' : 'gray.700'}>
                                Managed By TCAP
                            </Text>
                            <Switch
                                isChecked={values.managedByTCAP}
                                onChange={(e) =>
                                    setFieldValue(
                                        'managedByTCAP',
                                        e.target.checked,
                                    )
                                }
                                colorScheme="messenger"
                                size="md"
                            />
                        </Flex>

                        {/* Accordion sections */}
                        <Box flex="1" overflowY="auto">
                            <Accordion
                                allowMultiple
                                defaultIndex={[0]}
                                borderColor={accordionBorderColor}>
                                {/* User Details */}
                                <AccordionItem
                                    borderColor={accordionBorderColor}
                                    mb={2}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    overflow="hidden">
                                    <AccordionSectionButton darkMode={darkMode}>
                                        User Details
                                    </AccordionSectionButton>
                                    <AccordionPanel px={4} py={4} bg={panelBg}>
                                        <Stack spacing={1}>
                                            {/* First Name */}
                                            <FormControl
                                                sx={{ margin: '0 !important' }}>
                                                <Stack
                                                    direction="row"
                                                    spacing={stackSpacing}
                                                    sx={stackStyling()}>
                                                    <FormLabel
                                                        sx={formLabelStyling(
                                                            darkMode,
                                                        )}>
                                                        First Name
                                                    </FormLabel>
                                                    <Box flex="1" pt="15px">
                                                        <Field name="firstName">
                                                            {({ field }) => (
                                                                <Input
                                                                    {...field}
                                                                    isDisabled={
                                                                        !values.managedByTCAP
                                                                    }
                                                                    placeholder="First Name"
                                                                />
                                                            )}
                                                        </Field>
                                                    </Box>
                                                </Stack>
                                            </FormControl>

                                            {/* Last Name */}
                                            <FormControl
                                                sx={{ margin: '0 !important' }}>
                                                <Stack
                                                    direction="row"
                                                    spacing={stackSpacing}
                                                    sx={stackStyling()}>
                                                    <FormLabel
                                                        sx={formLabelStyling(
                                                            darkMode,
                                                        )}>
                                                        Last Name
                                                    </FormLabel>
                                                    <Box flex="1" pt="15px">
                                                        <Field name="lastName">
                                                            {({ field }) => (
                                                                <Input
                                                                    {...field}
                                                                    isDisabled={
                                                                        !values.managedByTCAP
                                                                    }
                                                                    placeholder="Last Name"
                                                                />
                                                            )}
                                                        </Field>
                                                    </Box>
                                                </Stack>
                                            </FormControl>

                                            {/* Email */}
                                            <FormControl
                                                sx={{ margin: '0 !important' }}>
                                                <Stack
                                                    direction="row"
                                                    spacing={stackSpacing}
                                                    sx={stackStyling()}>
                                                    <FormLabel
                                                        sx={formLabelStyling(
                                                            darkMode,
                                                        )}>
                                                        Email
                                                    </FormLabel>
                                                    <Box flex="1" pt="15px">
                                                        <Field name="email">
                                                            {({ field }) => (
                                                                <Input
                                                                    {...field}
                                                                    isDisabled={
                                                                        !values.managedByTCAP
                                                                    }
                                                                    type="email"
                                                                    placeholder="email@example.com"
                                                                />
                                                            )}
                                                        </Field>
                                                    </Box>
                                                </Stack>
                                            </FormControl>

                                            {/* Timezone — removed */}

                                            {/* Language — removed */}

                                            {/* Display in Directory — removed */}
                                        </Stack>
                                    </AccordionPanel>
                                </AccordionItem>

                                {/* Contact Details — removed */}

                                {/* Licences */}
                                <AccordionItem
                                    borderColor={accordionBorderColor}
                                    mb={2}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    overflow="hidden">
                                    <AccordionSectionButton darkMode={darkMode}>
                                        Licences
                                    </AccordionSectionButton>
                                    <AccordionPanel px={4} py={4} bg={panelBg}>
                                        <AddUserLicenceTree
                                            catalogue={catalogue}
                                            value={values.selectedLicenceIds}
                                            onChange={(ids) =>
                                                setFieldValue(
                                                    'selectedLicenceIds',
                                                    ids,
                                                )
                                            }
                                            darkMode={darkMode}
                                            isDisabled={!values.managedByTCAP}
                                        />
                                    </AccordionPanel>
                                </AccordionItem>

                                {/* Phone Numbers */}
                                <AccordionItem
                                    borderColor={accordionBorderColor}
                                    mb={2}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    overflow="hidden">
                                    <AccordionSectionButton darkMode={darkMode}>
                                        Phone Numbers
                                    </AccordionSectionButton>
                                    <AccordionPanel px={4} py={4} bg={panelBg}>
                                        <Stack spacing={1}>
                                            {/* Primary Number */}
                                            <FormControl
                                                sx={{ margin: '0 !important' }}>
                                                <Stack
                                                    direction="row"
                                                    spacing={stackSpacing}
                                                    sx={stackStyling()}>
                                                    <FormLabel
                                                        sx={formLabelStyling(
                                                            darkMode,
                                                        )}>
                                                        Primary Number
                                                    </FormLabel>
                                                    <Box flex="1" pt="15px">
                                                        {(() => {
                                                            const selectedNum =
                                                                fullNumberList.find(
                                                                    (n) =>
                                                                        n.numberID ===
                                                                        values.primaryNumberId,
                                                                ) || null;
                                                            return (
                                                                <>
                                                                    <Tooltip
                                                                        label="Assign a base platform licence before allocating a phone number"
                                                                        isDisabled={
                                                                            hasBaseLicence
                                                                        }
                                                                        hasArrow
                                                                        placement="top">
                                                                        <Box>
                                                                            <NumberSelect
                                                                                options={
                                                                                    fullNumberList
                                                                                }
                                                                                value={
                                                                                    selectedNum
                                                                                }
                                                                                getOptionLabel={(
                                                                                    n,
                                                                                ) =>
                                                                                    n.telephoneNumber
                                                                                }
                                                                                getOptionValue={(
                                                                                    n,
                                                                                ) =>
                                                                                    n.numberID
                                                                                }
                                                                                onChange={(
                                                                                    item,
                                                                                ) =>
                                                                                    setFieldValue(
                                                                                        'primaryNumberId',
                                                                                        item?.numberID ||
                                                                                            '',
                                                                                    )
                                                                                }
                                                                                placeholder="Not Allocated"
                                                                                isClearable
                                                                                isLoading={
                                                                                    isLoadingNumbers
                                                                                }
                                                                                isDisabled={
                                                                                    isLoadingNumbers ||
                                                                                    !hasBaseLicence ||
                                                                                    !values.managedByTCAP
                                                                                }
                                                                                chakraStyles={dropDownFormStyle(
                                                                                    darkMode,
                                                                                )}
                                                                                menuPortalTarget={
                                                                                    document.body
                                                                                }
                                                                                styles={{
                                                                                    menuPortal:
                                                                                        (
                                                                                            base,
                                                                                        ) => ({
                                                                                            ...base,
                                                                                            zIndex: 9999,
                                                                                        }),
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    </Tooltip>
                                                                    {selectedNum?.trunkType && (
                                                                        <Text
                                                                            fontSize="xs"
                                                                            color={
                                                                                darkMode
                                                                                    ? 'gray.400'
                                                                                    : 'gray.500'
                                                                            }
                                                                            mt={
                                                                                1
                                                                            }>
                                                                            Trunk:{' '}
                                                                            {
                                                                                selectedNum.trunkType
                                                                            }
                                                                        </Text>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </Box>
                                                </Stack>
                                            </FormControl>
                                        </Stack>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Accordion>
                        </Box>

                        {/* Footer buttons */}
                        <Flex
                            align="center"
                            justify="space-between"
                            pt={4}
                            mt={2}
                            borderTopWidth="1px"
                            borderColor={borderColor}>
                            <HStack spacing={3}>
                                <Button
                                    colorScheme="messenger"
                                    onClick={submitForm}
                                    isDisabled={!values.managedByTCAP}
                                    data-walkthroughid="userEditDrawerSaveBtn">
                                    Save
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={onClose}
                                    data-walkthroughid="userEditDrawerCancelBtn">
                                    Cancel
                                </Button>
                            </HStack>
                        </Flex>
                    </Flex>
                );
            }}
        </Formik>
    );
}
